import React, { useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Paper, Typography } from '@mui/material';
import { spaces, palette, customSizes, borderRadius } from '../../../assets/styles/themes/tokens';
import { MindItem } from '@kimuno/api-sdk/lib/types/responses/MindItem';
import { useSelector } from 'react-redux';
import { StoreType } from '../../../reducers';
import { selectMindItem } from '../../../reducers/mindItems.selectors';
import { useReaderContext } from '../../Reader/ReaderContext';
import { Link } from 'found';
import { routes } from '../../../constants';

interface NumberedPagesProps {
  pages: MindItem<'document'>['pages'] | MindItem<'read-only'>['pages'];
  currentPage: number;
  mindItemId: string;
}

const NumberedPages = React.memo<NumberedPagesProps>(({
  pages,
  currentPage,
  mindItemId,
}) => {
  const { t } = useTranslation();
  const imageAlt = t('Page {{number}} preview', { number: currentPage });
  const lastScrolledPage = useRef<number>(-1);

  const scrollRef = useCallback((node: HTMLDivElement | null) => {
    if (!node || lastScrolledPage.current === currentPage) return;
    lastScrolledPage.current = currentPage;
    const img = node.querySelector('img');
    if (!img) return;

    const doScroll = () => node.scrollIntoView({ block: 'center' });

    // Scroll immediately if image is already loaded, otherwise wait for load event
    // to ensure accurate scroll position after image dimensions are known.
    if (img.complete) {
      requestAnimationFrame(doScroll);
    } else {
      img.addEventListener('load', doScroll, { once: true });
    }
  }, [currentPage]);

  return (
    <Box sx={{
      height: '100%',
      display: 'flex',
      flexDirection: 'column',
    }}>
      {
        pages.map((page, i) => {
          const index = i + 1;
          const isCurrentPage = currentPage === index;

          const component = (
            <Box ref={isCurrentPage ? scrollRef : undefined} key={index}>
              <Link
                to={{
                  pathname: `/${routes.reader.root}/${mindItemId}`,
                  query: { page: `${index}` }
                }}
                data-testid={`page-preview-${index}`}
                {...(isCurrentPage && { 'data-testid-current-page': 'true' })}
              >
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    alignItems: 'center',
                    gap: spaces.xxs,
                    marginBottom: spaces.sm,
                  }}
                >
                  <Box
                    component='img'
                    src={page.w58}
                    alt={imageAlt}
                    sx={{
                      width: '100%',
                      height: '100%',
                      border: isCurrentPage
                        ? `1px solid ${palette.primary.main}`
                        : `1px solid ${palette.elevation.outlined}`,
                      borderRadius: borderRadius.xs,
                    }}
                  />
                  <Typography variant='caption' color={palette.text.secondary}>
                    {index}
                  </Typography>
                </Box>
              </Link>
            </Box>
          );

          return component;
        })
      }
    </Box>
  );
});

const PagePreviewList = () => {
  const { t } = useTranslation();
  const { currentPage, mindItemId, fullScreenMode } = useReaderContext();
  const mindItem = useSelector(
    (state: StoreType) => selectMindItem(state, mindItemId)
  ) as MindItem<'document'> | MindItem<'read-only'>;
  if (!mindItem) return null;

  return (
    <Paper 
      elevation={2}
      sx={{
        borderRadius: spaces.sm,
        color: palette.text.primary,
        width: customSizes.pagePreviewList.width,
        marginY: customSizes.readerPage.paddingY,
        display: fullScreenMode ? 'none' : 'flex',
        flexDirection: 'column'
      }}
    >
      <Box
        sx={{
          paddingTop: spaces.md,
          paddingBottom: spaces.sm,
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
        }}
        data-testid='page-preview-num-of-pages'>
        <Typography variant='overline'>
          {mindItem.pages.length} {mindItem.pages.length > 1 ? t('Pages') : t('Page')}
        </Typography>
      </Box>
      <Box sx={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        flexDirection: 'column',
        overflowY: 'scroll',
      }}>
        <NumberedPages 
          pages={mindItem.pages}
          currentPage={currentPage}
          mindItemId={mindItemId}
        />
      </Box>
    </Paper>
  );
};

export default PagePreviewList;
